import React, { useEffect } from "react"
import { Tab, Tabs, TabsProps } from "@mui/material"
import { useLocation, useNavigate } from "react-router-dom"
import { makeStyles } from '@mui/styles';
import { useGenericViewerOpened, useTranslations } from "hooks";
import { useCallback, useMemo } from "react";
import { useUrlFilterValueToString } from "hooks/filter/useUrlFilterValueToString";

/**
 * The simpliest way to use material-ui tabs by just passing an array of menu
 */
export const GenericTabsMenu = ({ menus, path, viewerUrl, canNavigate, showNavConfirmation, ...props }: GenericTabsMenuType) => {
    const [
        NAVIGATION_CONFIRMATION
    ] = useTranslations(i18n);
    const classes = useStyles();
    const navigate = useNavigate()
    const filterValueToString = useUrlFilterValueToString();
    const viewerOpened = useGenericViewerOpened(viewerUrl || '');
    const location = useLocation()

    const menuIndex = useMemo(() => menus?.findIndex((item) => location.pathname?.includes(item.path)) || 0, [menus, location])

    useEffect(() => {
        if (menuIndex === -1 && viewerOpened) {
            const [menu] = menus || []
            menu && navigate(`${menu.path}?${filterValueToString}`)
        }
    }, [menuIndex, viewerOpened])

    const getDirtyClass = useCallback((index: number) => {
        return (canNavigate === false || showNavConfirmation) && index === menuIndex ? classes.notSavedPage : ''
    }, [menuIndex, canNavigate, showNavConfirmation])

    const handleMenuClick = async (index: number, menu: GenericTabsMenuItemType) => {
        /**
         * Todo : Preventing navigation should not be inside this component
         * It must be handle by "onNavigate" callback
         */
        if (canNavigate === false) {
            return;
        }

        if (
            showNavConfirmation 
            && index !== menuIndex 
            && !window.confirm(NAVIGATION_CONFIRMATION)
        ) {
            return;
        }

        const result = await props.onNavigate?.(menu, index)

        if (result !== false) {
            navigate(`${menu.path}?${filterValueToString}`)
        }
    }
    
    return (
        <React.Fragment>
            <Tabs
                value={menuIndex || 0}
                indicatorColor="primary"
                textColor="primary"
                {...props}>
                {
                    menus?.map((menu: GenericTabsMenuItemType, index: number) => (
                        <Tab 
                            key={index} 
                            tabIndex={index} 
                            label={menu.label} 
                            onClick={() => handleMenuClick(index,menu)} 
                            className={`${classes.tabStyle} ${getDirtyClass(index)}`}
                            />
                    ))
                }
            </Tabs>
        </React.Fragment>
    )
}

const i18n = [
    'CosmeticFactory.Supplier.Dashboard.Messages.navigationConfirmation.title',
];

const useStyles = makeStyles({
    notSavedPage: {
        color:'#d9534f !important',
        TextDecorationColor:'#d9534f !important',
        borderRadius: '3px !important',
        '&:hover': {
            backgroundColor: '#e0514d !important',
            color:'#2c72ab !important',
            borderRadius: '3px !important',
            transition: '0.5s'
        },
    },
    tabStyle: {
        height: '25px !important',
        minHeight: '25px !important',
        paddingTop: '0px !important',
        paddingBottom: '0px !important',
    },
});

export type GenericTabsMenuType = TabsProps & {
    /**
     * Flag telling if clicking on tabs will change automatically the route
     * @deprecated use "onNavigate" callback instead to prevent navigation
     */
    autoNavigate?: boolean;
    /** 
     * Path currently active
     * @deprecated no need to pass it as props, it will be deducted by the component
     */
    path?: string | null;
    /** Menu to render */
    menus?: Array<GenericTabsMenuItemType>;

    /**
     * Flag telling if there is unsaved data on the current page, If this is the case you should see a dialog about this case
     * @deprecated this logic should be outside this component
     */
    showNavConfirmation?: boolean;

    /**
     * @deprecated use "onNavigate" callback instead to prevent navigation
     */
    canNavigate?: boolean;
    /**
     * Callback when user attempt to navigate
     * return false will prevent the event
     */
    onNavigate?: (menu?: GenericTabsMenuItemType, index?: number) => boolean | void | Promise<boolean | void>;

    /**
     * Url to track the viewer if it's opened
     * No need to pass it outside viewer context !!
     */
    viewerUrl?: string;
}

export type GenericTabsMenuItemType = {
    /** Target uri */
    path: string;
    /** Label of the path */
    label?: string;
    /** Custom option that allow user to see  */
    customOption?: string;
}

export type GenericTabsMenuItemProps = GenericTabsMenuItemType
